
import { supabase } from "@/integrations/supabase/client";
import { QueryResult } from "../types";
import { currencyConverter } from "@/services/currencyConverter";

/**
 * Queries related to financial results (income - cost)
 */
export const financialResultQueries = {
  // Get user financial result
  getUserFinancialResult: async (year?: number): Promise<QueryResult> => {
    try {
      const currentYear = year || new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1).toISOString();
      const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString();
      
      // Get user's default currency
      const { data: preferences, error: prefError } = await supabase
        .from('user_preferences')
        .select('default_currency_id, currencies:default_currency_id(id, code, symbol)')
        .single();
      
      if (prefError && prefError.code !== 'PGRST116') {
        console.error('Error fetching user preferences:', prefError);
      }
      
      const defaultCurrencyId = preferences?.default_currency_id || null;
      const defaultCurrency = preferences?.currencies || null;

      // Fetch contracts with costs and their currencies within the selected year
      const { data: contractsData, error: contractsError } = await supabase
        .from('contracts')
        .select(`
          id, 
          expected_cost, 
          currency_id, 
          currencies:currency_id(id, code, symbol),
          contract_types:contract_type_id(id, name, direction),
          signed_date
        `)
        .gte('signed_date', `${currentYear}-01-01`)
        .lte('signed_date', `${currentYear}-12-31`);
      
      if (contractsError) throw contractsError;
      
      console.log(`Fetched ${contractsData.length} contracts for year ${currentYear}`);
      
      // Calculate total income and cost, with currency tracking
      let totalCost = 0;
      let totalIncome = 0;
      
      // Track values by currency for transparency
      const valuesByCurrency: Record<string, { 
        currencyId: string; 
        total: number;
        totalCost: number;
        totalIncome: number;
        symbol: string;
        code: string;
      }> = {};
      
      // Process each contract
      for (const contract of contractsData) {
        const cost = parseFloat(String(contract.expected_cost || '0'));
        const currencyId = contract.currency_id || 'unknown';
        
        // Check if contract has the 'incoming' direction, otherwise treat as cost
        const isIncoming = contract.contract_types?.direction === 'incoming';
        
        // Initialize currency in tracking object if not exists
        if (!valuesByCurrency[currencyId]) {
          valuesByCurrency[currencyId] = {
            currencyId,
            total: 0,
            totalCost: 0,
            totalIncome: 0,
            symbol: contract.currencies?.symbol || '$',
            code: contract.currencies?.code || 'USD'
          };
        }
        
        // Update currency-specific totals
        valuesByCurrency[currencyId].total += cost;
        
        if (isIncoming) {
          valuesByCurrency[currencyId].totalIncome += cost;
          totalIncome += cost; // This will be converted later if needed
        } else {
          valuesByCurrency[currencyId].totalCost += cost;
          totalCost += cost; // This will be converted later if needed
        }
      }
      
      // If default currency is set, convert all amounts to default currency
      let convertedTotalCost = 0;
      let convertedTotalIncome = 0;
      let conversionRates: Record<string, number> = {};
      
      if (defaultCurrencyId) {
        // Convert each currency's amount to the default currency
        for (const currencyId in valuesByCurrency) {
          if (currencyId === defaultCurrencyId) {
            // No conversion needed for default currency
            convertedTotalCost += valuesByCurrency[currencyId].totalCost;
            convertedTotalIncome += valuesByCurrency[currencyId].totalIncome;
            conversionRates[currencyId] = 1;
            continue;
          }
          
          try {
            // Convert cost
            if (valuesByCurrency[currencyId].totalCost > 0) {
              const costConversion = await currencyConverter.convert(
                valuesByCurrency[currencyId].totalCost,
                currencyId,
                defaultCurrencyId
              );
              convertedTotalCost += costConversion.convertedAmount;
              
              // Store conversion rate for reference
              if (!conversionRates[currencyId]) {
                conversionRates[currencyId] = costConversion.rate;
              }
            }
            
            // Convert income
            if (valuesByCurrency[currencyId].totalIncome > 0) {
              const incomeConversion = await currencyConverter.convert(
                valuesByCurrency[currencyId].totalIncome,
                currencyId,
                defaultCurrencyId
              );
              convertedTotalIncome += incomeConversion.convertedAmount;
              
              // Store conversion rate if not already stored
              if (!conversionRates[currencyId]) {
                conversionRates[currencyId] = incomeConversion.rate;
              }
            }
          } catch (error) {
            console.error(`Error converting currency ${currencyId} to ${defaultCurrencyId}:`, error);
            // Fall back to unconverted amounts
            convertedTotalCost += valuesByCurrency[currencyId].totalCost;
            convertedTotalIncome += valuesByCurrency[currencyId].totalIncome;
          }
        }
      } else {
        // No default currency, use unconverted amounts
        convertedTotalCost = totalCost;
        convertedTotalIncome = totalIncome;
      }
      
      // Calculate final result
      const originalResult = totalIncome - totalCost;
      const convertedResult = convertedTotalIncome - convertedTotalCost;
      
      return {
        data: { 
          result: originalResult,
          totalCost,
          totalIncome,
          convertedResult,
          convertedTotalCost,
          convertedTotalIncome,
          valuesByCurrency: Object.values(valuesByCurrency),
          conversionRates,
          defaultCurrencyId,
          defaultCurrency: defaultCurrency ? {
            id: defaultCurrency.id,
            code: defaultCurrency.code,
            symbol: defaultCurrency.symbol
          } : null
        },
        error: null
      };
    } catch (error) {
      console.error("Error fetching user financial result:", error);
      return {
        data: { 
          result: 0,
          totalCost: 0,
          totalIncome: 0,
          convertedResult: 0,
          convertedTotalCost: 0,
          convertedTotalIncome: 0,
          valuesByCurrency: [],
          conversionRates: {},
          defaultCurrencyId: null,
          defaultCurrency: null
        },
        error
      };
    }
  }
};
